import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Loader2, AtSign } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Textarea con autocomplete de menciones @admin.
 *
 * Props:
 *  - onSubmit(contenido: string, menciones: string[], notificarPorEmail: bool)
 *  - saving: bool
 *  - placeholder: string
 *  - onTyping: fn opcional
 */
export default function NotaMencionesInput({ onSubmit, saving, placeholder, onTyping }) {
  const [texto, setTexto] = useState('');
  const [notificarEmail, setNotificarEmail] = useState(false);
  const [mencionesAdmins, setMencionesAdmins] = useState([]); // [{id,nombre,email}]
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerOpts, setPickerOpts] = useState([]);
  const [pickerIdx, setPickerIdx] = useState(0);
  const [pickerAt, setPickerAt] = useState(null); // posición del "@" en el texto
  const textareaRef = useRef(null);
  const pickerRef = useRef(null);

  // Buscar admins con debounce
  useEffect(() => {
    if (!showPicker) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/bandeja-unificada/admins/buscar${pickerQuery ? `?q=${encodeURIComponent(pickerQuery)}` : ''}`);
        setPickerOpts(r.data || []);
        setPickerIdx(0);
      } catch (e) { console.error(e); }
    }, 150);
    return () => clearTimeout(t);
  }, [pickerQuery, showPicker]);

  // Cerrar picker si se clickea fuera
  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target) && textareaRef.current !== e.target) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [showPicker]);

  const handleChange = (e) => {
    const value = e.target.value;
    setTexto(value);
    onTyping?.();
    // Detectar "@" seguido de texto (no separado por espacios) desde la posición del cursor
    const cursor = e.target.selectionStart;
    const antes = value.slice(0, cursor);
    const match = antes.match(/@([\w.\-]{0,20})$/);
    if (match) {
      const at = cursor - match[0].length;
      setPickerAt(at);
      setPickerQuery(match[1]);
      setShowPicker(true);
    } else {
      setShowPicker(false);
      setPickerAt(null);
    }
  };

  const insertarMencion = useCallback((admin) => {
    if (pickerAt === null || !textareaRef.current) return;
    const ta = textareaRef.current;
    const antes = texto.slice(0, pickerAt);
    const despues = texto.slice(ta.selectionStart);
    // Preferimos el nombre; si sólo tenemos email, usamos la parte previa al @.
    // Reemplazamos espacios y '@' internos por '_' para que el token quede como palabra única
    // ("@Juan_Perez") — así el resaltado y las comparaciones son estables.
    const rawName = (admin.nombre && admin.nombre.trim()) ? admin.nombre.trim() : (admin.email || '').split('@')[0];
    const token = rawName.replace(/[@\s]+/g, '_');
    const tag = `@${token}`;
    const nuevo = `${antes}${tag} ${despues}`;
    setTexto(nuevo);
    if (!mencionesAdmins.find(m => m.id === admin.id)) {
      setMencionesAdmins([...mencionesAdmins, { ...admin, _tag: tag }]);
    }
    setShowPicker(false);
    setPickerAt(null);
    setTimeout(() => {
      const pos = antes.length + tag.length + 1;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    }, 0);
  }, [pickerAt, texto, mencionesAdmins]);

  const handleKeyDown = (e) => {
    if (showPicker && pickerOpts.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setPickerIdx((pickerIdx + 1) % pickerOpts.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setPickerIdx((pickerIdx - 1 + pickerOpts.length) % pickerOpts.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertarMencion(pickerOpts[pickerIdx]);
      } else if (e.key === 'Escape') {
        setShowPicker(false);
      }
    }
  };

  const enviar = async () => {
    if (!texto.trim()) return;
    // Filtrar menciones cuyos tokens siguen presentes en el texto
    const activos = mencionesAdmins.filter(m => m._tag && texto.includes(m._tag));
    await onSubmit(texto.trim(), activos.map(m => m.id), notificarEmail && activos.length > 0);
    setTexto('');
    setMencionesAdmins([]);
    setNotificarEmail(false);
  };

  return (
    <div className="border-t pt-3 space-y-2">
      <div className="relative">
        <textarea
          ref={textareaRef}
          className="w-full border rounded p-2 text-sm resize-none"
          rows={3}
          placeholder={placeholder || 'Escribí una nota interna. Usá @ para mencionar a un admin.'}
          value={texto}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          data-testid="input-nueva-nota"
        />
        {showPicker && pickerOpts.length > 0 && (
          <div
            ref={pickerRef}
            className="absolute z-50 left-2 bottom-full mb-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto w-64"
            data-testid="mencion-picker"
          >
            {pickerOpts.map((a, i) => (
              <button
                key={a.id}
                onClick={() => insertarMencion(a)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === pickerIdx ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                data-testid={`mencion-opt-${a.id}`}
              >
                <div className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center shrink-0">
                  {(a.nombre || a.email || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{a.nombre || a.email}</p>
                  <p className="text-[10px] text-gray-500 truncate">{a.email}</p>
                </div>
                <span className="text-[10px] text-gray-400 uppercase">{a.rol === 'superadmin' ? 'super' : a.rol?.slice(0, 5)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {mencionesAdmins.length > 0 && (
        <div className="flex items-center flex-wrap gap-2">
          <span className="text-xs text-gray-500 flex items-center gap-1"><AtSign className="w-3 h-3" /> Notificar a:</span>
          {mencionesAdmins.map(m => (
            <span
              key={m.id}
              className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 rounded-full px-2 py-0.5 text-xs"
              data-testid={`mencion-chip-${m.id}`}
            >
              {m._tag || `@${m.nombre || m.email}`}
              <button
                onClick={() => setMencionesAdmins(mencionesAdmins.filter(x => x.id !== m.id))}
                className="hover:text-purple-900"
              >×</button>
            </span>
          ))}
          <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer ml-auto">
            <input
              type="checkbox"
              checked={notificarEmail}
              onChange={e => setNotificarEmail(e.target.checked)}
              data-testid="check-notificar-email"
            />
            También enviar por email
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={enviar}
          disabled={saving || !texto.trim()}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="btn-guardar-nota"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Guardar</>}
        </Button>
      </div>
    </div>
  );
}
